import { FC, HTMLAttributes, KeyboardEvent, ReactNode, SyntheticEvent } from 'react';
import { Ban } from 'lucide-react';
import { StudentSkillStates } from '../../models/StudentSkillState';
import { AnimatedFocusPill } from '../UnitFocus/AnimatedFocusPill';
import { Unit } from '../../models/Unit';
import CheckpointIcon from '../../assets/CheckpointIcon/TargetSkillIcon';
import VisualProgressBar from './VisualProgressBar';
import { useStudentSkillStates } from '../../custom-hooks/useStudentSkillStates';
import { isSkillCompleted } from '../../businessLogicService/skillCompletionUtils';

export interface UnitCardData {
  id: string;
  name: string;
  summary?: string;
  completedCheckpoints: number;
  totalCheckpoints: number;
  completedSkills: number;
  totalSkills: number;
  isSelected?: boolean;
}

interface UnitCardProps {
  // Core unit data
  unit: Unit;

  // Skill data for calculations (if provided, will override unit props)
  // unitSkills?: StudentSkillStates[];

  // Pre-calculated metrics (if provided, will override skill calculations)
  // skillMetrics?: {
  //   totalCheckpoints: number;
  //   completedCheckpoints: number;
  //   totalSkills: number;
  //   completedSkills: number;
  // };

  // Display options
  /** DOM id on the card wrapper, used as a scroll anchor for the focus unit. */
  id?: string;
  selected?: boolean;
  hideButtons?: boolean;
  /** When true, render the persistent focus indicator + highlighted border (US-8, TEAMC-3148). */
  focused?: boolean;
  /** Optional node shown beside the focus badge (e.g. the student avatar on All Units). */
  focusAccessory?: ReactNode;
  // When the teacher has "Other units blocked" on, every unit except the focus reads as blocked (US-9):
  // the card shows a locked icon, hides its CTA, and becomes a button that raises onBlockedActivate.
  blocked?: boolean;

  // Actions
  onExplore?: (unitId: string) => void;
  // Fired when a blocked card is activated (click/Enter/Space); the parent opens the explanation tooltip.
  onBlockedActivate?: (e: SyntheticEvent) => void;
}

/**
 * Utility function to calculate skill metrics from unit skills (matching original dashboard pattern)
 */
const calculateSkillMetrics = (unitSkills: StudentSkillStates[]) => {
  if (!unitSkills || unitSkills.length === 0) {
    return {
      totalSkills: 0,
      completedSkills: 0,
      totalCheckpoints: 0,
      completedCheckpoints: 0,
      // Original dashboard pattern metrics
      unitSkills: {
        total: 0,
        completed: 0,
        remaining: 0,
      },
      supportSkills: {
        total: 0,
        completed: 0,
        remaining: 0,
      },
      extensionSkills: {
        total: 0,
        completed: 0,
      },
    };
  }

  const totalSkills = unitSkills.length;
  const completedSkills = unitSkills.filter(isSkillCompleted).length;

  // Checkpoints are "Target" type skills (same as "Unit" type in progress calculation)
  const checkpointSkills = unitSkills.filter(
    skill => skill.targetSkillType === 'Target' || skill.targetSkillType === 'Unit',
  );
  const totalCheckpoints = checkpointSkills.length;
  const completedCheckpoints = checkpointSkills.filter(isSkillCompleted).length;

  // 🎯 ORIGINAL DASHBOARD PATTERN: Break down by skill types
  const unitTypeSkills = unitSkills.filter(skill => skill.targetSkillType === 'Unit');
  const supportTypeSkills = unitSkills.filter(skill => skill.targetSkillType === 'Support');
  const extensionTypeSkills = unitSkills.filter(skill => skill.targetSkillType === 'Extension');

  const unitSkillsCompleted = unitTypeSkills.filter(isSkillCompleted).length;
  const supportSkillsCompleted = supportTypeSkills.filter(isSkillCompleted).length;
  const extensionSkillsCompleted = extensionTypeSkills.filter(isSkillCompleted).length;

  const metrics = {
    totalSkills,
    completedSkills,
    totalCheckpoints,
    completedCheckpoints,
    // Original dashboard pattern metrics
    unitSkills: {
      total: unitTypeSkills.length,
      completed: unitSkillsCompleted,
      remaining: unitTypeSkills.length - unitSkillsCompleted,
    },
    supportSkills: {
      total: supportTypeSkills.length,
      completed: supportSkillsCompleted,
      remaining: supportTypeSkills.length - supportSkillsCompleted,
    },
    extensionSkills: {
      total: extensionTypeSkills.length,
      completed: extensionSkillsCompleted,
    },
  };

  return metrics;
};

const UnitCard: FC<UnitCardProps> = ({
  unit,
  // unitSkills,
  // skillMetrics,
  id,
  selected = false,
  hideButtons = false,
  focused = false,
  focusAccessory,
  blocked = false,
  onExplore,
  onBlockedActivate,
}) => {
  const onBlockedKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onBlockedActivate?.(e);
    }
  };

  // A blocked card becomes a button that opens the explanation tooltip; otherwise it's inert.
  const blockedInteraction: HTMLAttributes<HTMLDivElement> = blocked
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': `Unit ${unit.name.split(':')[0]} is blocked. Select to learn why.`,
        onClick: onBlockedActivate,
        onKeyDown: onBlockedKeyDown,
      }
    : {};

  const { studentSkillStates } = useStudentSkillStates({ unitToFilterBy: unit.id });
  const metrics = studentSkillStates
    ? calculateSkillMetrics(studentSkillStates)
    : {
        totalSkills: 0,
        completedSkills: 0,
        totalCheckpoints: 0,
        completedCheckpoints: 0,
      };

  // US-8: the focused unit gets an indigo border to match the "MY NEW FOCUS" indicator.
  const borderColorClass = focused
    ? 'tw-border-indigo-300 tw-rounded-2xl tw-border-t-4 tw-border-r-4 tw-border-b-[8px] tw-border-l-4 tw-border-[#7D91FD] tw-bg-[#FEFBF9]'
    : 'tw-border-[#F1F0EE]';

  return (
    <div
      id={id}
      className={`tw-w-full tw-relative tw-self-stretch tw-p-6 tw-bg-white tw-inline-flex tw-flex-col tw-rounded-3xl tw-justify-start tw-items-center tw-gap-9 tw-border-4 tw-border-solid ${borderColorClass}${
        blocked ? ' tw-cursor-pointer' : ''
      }`}
      {...blockedInteraction}
    >
      {focused && (
        <div className="tw-absolute tw--top-3 tw-left-4 tw-z-10 tw-flex tw-items-center">
          <AnimatedFocusPill />
          {focusAccessory}
        </div>
      )}
      {blocked && (
        <span className="tw-absolute tw-right-6 tw-top-6 tw-text-red-400" aria-hidden>
          <Ban className="tw-h-8 tw-w-8" />
        </span>
      )}
      <div
        className="tw-self-stretch tw-p-6 tw-inline-flex tw-flex-col tw-items-center tw-border-none tw-gap-4"
        aria-label={unit.name}
      >
        <div className="tw-self-stretch tw-flex tw-flex-col tw-justify-start tw-items-start tw-gap-2">
          <h3 className="tw-m-0 tw-justify-center">
            <span className="tw-text-black tw-text-2xl tw-font-bold tw-font-['Sora']">
              Unit {unit.name.split(':')[0]}:
            </span>
            <span className="tw-text-black tw-text-2xl tw-font-bold tw-font-['Sora'] tw-uppercase"> </span>
            <span className="tw-text-black tw-text-2xl tw-font-bold tw-font-['Sora']">{unit.summary}</span>
          </h3>
        </div>

        <div className="unit-progress tw-self-stretch tw-flex tw-justify-start tw-items-center tw-gap-2 tw-w-full tw-flex-col">
          <div className="tw-self-stretch tw-inline-flex tw-justify-start tw-items-center tw-gap-2 tw-pt-2 tw-pb-4">
            <div className="tw-flex tw-justify-start tw-items-center tw-gap-1">
              <div className="tw-w-9 tw-h-9">
                <CheckpointIcon />
              </div>
              <div className="tw-justify-center">
                <span className="tw-text-zinc-700 tw-text-lg tw-font-bold tw-font-atkinson tw-leading-snug">
                  {metrics.completedCheckpoints}
                </span>
                <span className="tw-text-zinc-700 tw-text-lg tw-font-normal tw-font-atkinson tw-leading-snug">
                  /{metrics.totalCheckpoints}{' '}
                </span>
                <span className="tw-text-zinc-700 tw-text-lg tw-font-atkinson tw-leading-snug">Checkpoints</span>
              </div>
            </div>
            |
            <div className="tw-justify-center tw-text-zinc-700 tw-text-base tw-font-atkinson tw-leading-none">
              <span className="tw-font-bold">{metrics.completedSkills}</span>/{metrics.totalSkills}{' '}
              <span className="tw-font-bold">total skills</span>
            </div>
          </div>

          {/* Visual Progress Bar - using reusable component */}
          <VisualProgressBar
            totalItems={metrics.totalSkills}
            completedItems={metrics.completedSkills}
            showCheckpointIndicator={true}
            targetSkillsCompleted={
              metrics.completedCheckpoints === metrics.totalCheckpoints && metrics.totalCheckpoints > 0
            }
          />
        </div>
        {!blocked &&
          !hideButtons &&
          (selected ? (
            <button
              className="tw-cursor-pointer tw-justify-start tw-text-zinc-100 tw-bg-blue-600 tw-rounded-3xl tw-text-lg tw-font-bold tw-font-['Atkinson_Hyperlegible'] tw-leading-7 tw-h-10 tw-px-6 tw-py-2 tw-inline-flex tw-items-center tw-gap-3 tw-border-none"
              onClick={() => onExplore && onExplore(unit.id)}
              aria-label="Jump back in"
              tabIndex={0}
            >
              Jump back in
            </button>
          ) : metrics.completedCheckpoints === metrics.totalCheckpoints && metrics.totalCheckpoints > 0 ? (
            <button
              className="tw-cursor-pointer tw-justify-start tw-text-blue-500 tw-bg-transparent tw-rounded-3xl tw-text-lg tw-font-bold tw-font-atkinson tw-leading-7 tw-h-10 tw-px-6 tw-py-2 tw-inline-flex tw-items-center tw-gap-3 tw-border-none"
              onClick={() => onExplore && onExplore(unit.id)}
              aria-label="Review completed unit"
              tabIndex={0}
            >
              Review Unit
            </button>
          ) : (
            <button
              className="tw-cursor-pointer tw-justify-start tw-text-black tw-bg-white tw-rounded-3xl tw-text-lg tw-font-bold tw-font-['Atkinson_Hyperlegible'] tw-leading-7 tw-h-10 tw-px-6 tw-py-2 tw-inline-flex tw-items-center tw-gap-3 tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-neutral-800"
              onClick={() => onExplore && onExplore(unit.id)}
              aria-label="Explore unit"
              tabIndex={0}
            >
              Explore unit
            </button>
          ))}
      </div>
    </div>
  );
};

export default UnitCard;
